'use client';

import { fitToViewport } from '@ps/functions/media/fitToViewport';
import { Play } from 'lucide-react';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';

import { getMessageMediaSize } from '../../functions/getMessageMediaSize';
import type {
  ChatVideoMessage,
  VideoContent,
  VideoThumbnail,
} from '../../types/ChatMessage';
import { LightboxVideoSlide } from './LightboxVideoSlide';
import { MessageBubbleShell } from './MessageBubbleShell';

interface Props {
  message: ChatVideoMessage;
  isOwn: boolean;
}

/** Fallback lightbox stage aspect when the content carries no dimensions. */
const kFallbackStageWidth = 1280;
const kFallbackStageHeight = 720;

/**
 * Video bubble. Renders a poster tile (the thumbnail, or the first
 * frame when no thumbnail is available) with a play overlay; tapping
 * opens the `react-photo-view` lightbox and plays the video full-screen.
 * One `PhotoProvider` per message, mirroring the image bubble and the
 * community-channels attachment lightbox.
 */
export function VideoMessage({ message, isOwn }: Props) {
  const { src, poster, size, stage } = useVideoInfo(message.content);

  if (!src) {
    return (
      <MessageBubbleShell message={message} isOwn={isOwn}>
        <div className="bg-surface-3 text-content-2 px-3.5 py-2 text-xs italic">
          Video unavailable
        </div>
      </MessageBubbleShell>
    );
  }

  return (
    <MessageBubbleShell message={message} isOwn={isOwn} bare>
      <PhotoProvider>
        <PhotoView
          width={stage.width}
          height={stage.height}
          render={({ attrs }) => (
            <LightboxVideoSlide attrs={attrs} src={src} poster={poster} />
          )}
        >
          <button
            type="button"
            className="bg-surface-2 relative block cursor-zoom-in overflow-hidden rounded-2xl"
            style={{ width: size.width, height: size.height }}
          >
            {poster ? (
              <img
                src={poster}
                alt=""
                width={size.width}
                height={size.height}
                className="h-full w-full object-cover"
                loading="lazy"
              />
            ) : (
              // No thumbnail — show the first frame via a muted, non-loading
              // <video>; the lightbox owns actual playback.
              <video
                src={src}
                muted
                playsInline
                preload="metadata"
                className="h-full w-full object-cover"
              />
            )}
            <div className="bg-scrim/30 absolute inset-0 flex items-center justify-center">
              <Play className="h-9 w-9 fill-white text-white" aria-hidden />
            </div>
          </button>
        </PhotoView>
      </PhotoProvider>
    </MessageBubbleShell>
  );
}

function useVideoInfo(content: VideoContent): {
  src: string | undefined;
  poster: string | undefined;
  size: { width: number; height: number };
  stage: { width: number; height: number };
} {
  // Orientation source: the thumbnail's width/height are required by the type
  // whenever a thumbnail is present, whereas the top-level video width/height
  // are optional and — for inbound DM videos — frequently missing. Prefer the
  // thumbnail dims so a portrait video isn't sized into a landscape stage.
  const dims = resolveVideoDimensions(content);
  const size = getMessageMediaSize(dims?.width, dims?.height);
  // react-photo-view fits IMAGE slides to the viewport itself, but a custom
  // `render` slide (our video) is drawn at the raw width/height we pass, at
  // scale 1 — so oversized dims overflow and crop. Pre-fit the aspect ratio to
  // the viewport so the whole video is visible. These dims only reach the
  // client-only slider, never the SSR'd bubble DOM, so reading `window` is safe.
  const stage = fitToViewport(
    dims?.width || kFallbackStageWidth,
    dims?.height || kFallbackStageHeight,
  );
  const src =
    'localFileUri' in content ? content.localFileUri : content.videoUrl;
  const poster = resolveThumbnail(content.thumbnail);
  return { src, poster, size, stage };
}

function resolveVideoDimensions(
  content: VideoContent,
): { width: number; height: number } | undefined {
  const thumb = content.thumbnail;
  if (thumb) return { width: thumb.width, height: thumb.height };
  if (content.width != null && content.height != null) {
    return { width: content.width, height: content.height };
  }
  return undefined;
}

function resolveThumbnail(
  thumb: VideoThumbnail | undefined,
): string | undefined {
  if (!thumb) return undefined;
  if ('localFileUri' in thumb) return thumb.localFileUri;
  return thumb.url;
}
